import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import DeploymentForm from '../components/DeploymentForm';
import { BetaBadge } from '../components/BetaDisclaimer';
import aiAppsService from '../services/aiAppsService';
import DeploymentOptions from '../components/DeploymentOptions';
import { PageHeader } from '../components/PageHeader';

const DeploymentManager = () => {
  const [deployedApps, setDeployedApps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedAppType, setSelectedAppType] = useState('all');
  const [initialAppType, setInitialAppType] = useState(null);
  const [availableApps, setAvailableApps] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const location = useLocation();
  const navigate = useNavigate();

  // Add new state for deployment metrics
  const [deploymentMetrics, setDeploymentMetrics] = useState({
    totalDeployments: 0,
    activeDeployments: 0,
    cpuUsage: 0,
    memoryUsage: 0,
    costToday: 0
  });

  // Get app type from query parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const appType = queryParams.get('appType');
    
    if (appType) {
      setInitialAppType(appType);
      // Clear the query parameter from the URL
      navigate('/deployment-manager', { replace: true });
    }
  }, [location, navigate]);

  // Load available apps from the service
  useEffect(() => {
    const loadApps = () => {
      try {
        let apps;
        if (selectedCategory === 'all') {
          apps = aiAppsService.getAllApps();
        } else {
          apps = aiAppsService.getAppsByCategory(selectedCategory);
        }
        
        // Format apps for the dropdown
        const formattedApps = [
          { id: 'all', name: 'All Apps' },
          ...apps.map(app => ({
            id: app.id,
            name: app.name,
            description: app.description
          }))
        ];
        
        setAvailableApps(formattedApps);
      } catch (error) {
        console.error('Error loading apps:', error);
        toast.error('Failed to load available apps');
      }
    };
    
    loadApps();
  }, [selectedCategory]);

  // App categories
  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'content', name: 'Content Creation' },
    { id: 'development', name: 'Development' },
    { id: 'research', name: 'Research' },
    { id: 'education', name: 'Education' },
    { id: 'business', name: 'Business' }
  ];

  useEffect(() => {
    fetchDeployedApps();
  }, [selectedAppType]);

  const fetchDeployedApps = async () => {
    try {
      setIsLoading(true);
      const queryParams = selectedAppType !== 'all' ? `?appType=${selectedAppType}` : '';
      const response = await axios.get(`/api/deployed-apps${queryParams}`);
      
      if (response.data.success) {
        setDeployedApps(response.data.data);
        
        // Update metrics
        const activeApps = response.data.data.filter(app => app.status === 'RUNNING');
        setDeploymentMetrics({
          totalDeployments: response.data.data.length,
          activeDeployments: activeApps.length,
          cpuUsage: activeApps.reduce((acc, app) => acc + (app.metrics?.cpu || 0), 0),
          memoryUsage: activeApps.reduce((acc, app) => acc + (app.metrics?.memory || 0), 0),
          costToday: activeApps.reduce((acc, app) => acc + (app.metrics?.cost || 0), 0)
        });
      } else {
        toast.error('Failed to fetch deployed apps');
      }
    } catch (error) {
      console.error('Error fetching deployed apps:', error);
      toast.error(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteApp = async (name, zone) => {
    if (window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
      try {
        setIsDeleting(true);
        const response = await axios.delete(`/api/deployed-apps/${name}?zone=${zone}`);
        
        if (response.data.success) {
          toast.success(`App ${name} deleted successfully`);
          fetchDeployedApps();
        } else {
          toast.error('Failed to delete app');
        }
      } catch (error) {
        console.error('Error deleting app:', error);
        toast.error(`Error: ${error.response?.data?.error || error.message}`);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="AI App Deployment Manager"
          subtitle="Deploy and manage AI applications on Google Cloud Platform"
          actions={
            <>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>GCP Connected</span>
              </div>
              <a 
                href="https://console.cloud.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View GCP Console
              </a>
            </>
          }
        />

        {/* Deployment Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Total Deployments</h3>
              <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{deploymentMetrics.totalDeployments}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Active Deployments</h3>
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{deploymentMetrics.activeDeployments}</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Resource Usage</h3>
              <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="mt-2 text-3xl font-semibold text-gray-900">{Math.round(deploymentMetrics.cpuUsage)}%</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Cost Today</h3>
              <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="mt-2 text-3xl font-semibold text-gray-900">${deploymentMetrics.costToday.toFixed(2)}</p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Configuration Area */}
          <div className="lg:w-2/3 space-y-6">
            {/* Deployment Configuration */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">Deploy New AI Application</h2>
                  <p className="mt-1 text-sm text-gray-500">Configure and deploy your AI application on GCP</p>
                </div>
              </div>
              <div className="p-6">
                <DeploymentForm 
                  onDeploymentSuccess={fetchDeployedApps} 
                  initialAppType={initialAppType}
                  availableApps={availableApps.filter(app => app.id !== 'all')}
                />
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">Advanced GCP Settings</h2>
                  <p className="mt-1 text-sm text-gray-500">Configure additional GCP-specific settings</p>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Network Configuration */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Network Configuration</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">VPC Network</label>
                        <select className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                          <option>default</option>
                          <option>custom-vpc-1</option>
                          <option>Create new VPC</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Subnet</label>
                        <select className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                          <option>default</option>
                          <option>subnet-us-central1</option>
                          <option>Create new subnet</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Security Configuration */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Security Configuration</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Service Account</label>
                        <select className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                          <option>Compute Engine default</option>
                          <option>Custom service account</option>
                          <option>Create new service account</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Network Tags</label>
                        <input 
                          type="text" 
                          placeholder="e.g., http-server,https-server"
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Scaling Configuration */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Scaling Configuration</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Auto Scaling</label>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center">
                            <input type="radio" name="scaling" className="mr-2" />
                            <span className="text-sm text-gray-700">Enabled</span>
                          </label>
                          <label className="flex items-center">
                            <input type="radio" name="scaling" className="mr-2" defaultChecked />
                            <span className="text-sm text-gray-700">Disabled</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Instance Count</label>
                        <input 
                          type="number" 
                          defaultValue="1"
                          min="1"
                          className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Cost Management */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Cost Management</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Budget Alert</label>
                        <div className="flex gap-2">
                          <input 
                            type="number" 
                            placeholder="Monthly budget"
                            className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <select className="border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                            <option>USD</option>
                            <option>EUR</option>
                            <option>GBP</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="flex items-center">
                          <input type="checkbox" className="mr-2" />
                          <span className="text-sm text-gray-700">Enable automatic shutdown when budget exceeded</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* API Documentation */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">API Documentation</h2>
                  <p className="mt-1 text-sm text-gray-500">Integration guide for deployed applications</p>
                </div>
              </div>
              <div className="p-6">
                <div className="prose max-w-none">
                  <p>Each deployed app exposes a REST API. Below is a sample integration:</p>
                  <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
{`fetch('http://[VM_IP]/api/review-code', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ${'{YOUR_API_KEY}'}'
  },
  body: JSON.stringify({
    codeSnippet: 'function add(a, b) { return a + b; }',
    language: 'javascript',
    context: 'Simple utility function'
  })
})`}
                  </pre>
                  <h3 className="text-sm font-medium text-gray-900 mt-4">Authentication</h3>
                  <p className="text-sm text-gray-600">
                    All API endpoints require authentication using an API key. You can generate API keys in the GCP Console.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Deployment Status */}
          <div className="lg:w-1/3 space-y-6">
            {/* Active Deployments */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4 flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Active Deployments</h2>
                    <p className="mt-1 text-sm text-gray-500">{deployedApps.length} applications running</p>
                  </div>
                  <button
                    onClick={fetchDeployedApps}
                    className="p-2 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-50"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                {isLoading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                  </div>
                ) : deployedApps.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="mt-2 text-sm font-medium text-gray-900">No active deployments</p>
                    <p className="mt-1 text-sm text-gray-500">Deploy your first AI application to get started</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {deployedApps.map((app) => (
                      <div key={app.id} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">{app.name}</h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {app.appType}
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            app.status === 'RUNNING' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {app.status || 'Unknown'}
                          </span>
                        </div>
                        <div className="mt-3 flex items-center justify-between text-sm">
                          <a 
                            href={app.apiUrl ? app.apiUrl.replace('/api', '/health') : `http://${app.externalIP}/health`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View Status
                          </a>
                          <button
                            onClick={() => handleDeleteApp(app.name, app.zone)}
                            disabled={isDeleting}
                            className="text-red-600 hover:text-red-800 disabled:text-gray-400 font-medium"
                          >
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Resource Usage */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">Resource Usage</h2>
                  <p className="mt-1 text-sm text-gray-500">Current resource consumption</p>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">CPU Usage</span>
                      <span className="text-gray-900 font-medium">{Math.round(deploymentMetrics.cpuUsage)}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full">
                      <div 
                        className="h-2 bg-blue-500 rounded-full" 
                        style={{ width: `${Math.min(Math.round(deploymentMetrics.cpuUsage), 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Memory Usage</span>
                      <span className="text-gray-900 font-medium">{Math.round(deploymentMetrics.memoryUsage)}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full">
                      <div 
                        className="h-2 bg-blue-500 rounded-full" 
                        style={{ width: `${Math.min(Math.round(deploymentMetrics.memoryUsage), 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add this style for the gradient-conic background
const style = document.createElement('style');
style.textContent = `
  .bg-gradient-conic {
    background-image: conic-gradient(var(--tw-gradient-stops));
  }
`;
document.head.appendChild(style);

export default DeploymentManager; 